import './App.css';
import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// Стартовый одностраничный компонент для главной страницы "СтройАгент"
// - Автоматическая тема (предполагается, что Tailwind настроен с dark mode: 'media' или 'class')
// - Верхнее горизонтальное меню
// - Левое скрывающееся выдвижное меню
// - Онлайн-видео (URL берётся из API-заглушки)
// - График выполнения (данные берутся из API-заглушки)
// - Блок текущих затрат (API-заглушка)
// - Простая авторизация с модальным окном и mock API

// ---------- API-заглушки (симуляция запросов) ----------
// Эти функции симулируют настоящий бэкенд. В реальном проекте замените их
// на fetch('/api/...') или axios запросы к вашему серверу.

function wait(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

async function mockLogin({ username, password }) {
  await wait(600);
  if (username === "admin" && password === "password") {
    return { ok: true, token: "mock-jwt-token", user: { name: "Администратор" } };
  }
  if (username && password) {
    return { ok: true, token: "mock-jwt-token", user: { name: username } };
  }
  return { ok: false, error: "Неверные данные" };
}

async function getVideoStreamUrl() {
  await wait(300);
  // В реальном приложении здесь будет URL на потоковую камеру (RTMP/HLS) или WebRTC сигнальный endpoint.
  // Для макета используем sample mp4 (можно заменить на HLS .m3u8 при необходимости)
  return {
    url: "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    meta: { camera: "Камера №1 — Главная стройплощадка" },
  };
}

async function fetchProgressData() {
  await wait(400);
  // Пример данных для графика выполнения (процент выполнения по датам)
  return [
    { date: "2025-09-01", complete: 5 },
    { date: "2025-09-08", complete: 12 },
    { date: "2025-09-15", complete: 22 },
    { date: "2025-09-22", complete: 35 },
    { date: "2025-09-29", complete: 48 },
    { date: "2025-10-06", complete: 58 },
    { date: "2025-10-13", complete: 68 },
    { date: "2025-10-18", complete: 75 },
  ];
}

async function fetchCosts() {
  await wait(350);
  return {
    totalPlanned: 1200000,
    totalActual: 930500,
    breakdown: [
      { name: "Материалы", amount: 520000 },
      { name: "Работа", amount: 280000 },
      { name: "Аренда техники", amount: 80000 },
      { name: "Прочие", amount: 50500 },
    ],
  };
}

// ----------------- Утилиты форматирования -----------------
function fmtCurrency(n) {
  return n.toLocaleString("ru-RU", { style: "currency", currency: "RUB", maximumFractionDigits: 0 });
}

// ----------------- Подкомпоненты -----------------
function TopBar({ onToggleSidebar, user, onOpenLogin }) {
  return (
    <header className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700 bg-white/60 dark:bg-gray-800/60 backdrop-blur">
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
          className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {/* simple hamburger */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="text-lg font-semibold">СтройАгент</div>
      </div>

      <nav className="flex items-center gap-4">
        <button className="hidden sm:inline-block px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">Проекты</button>
        <button className="hidden sm:inline-block px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">Отчёты</button>
        <button className="hidden sm:inline-block px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">Календарь</button>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm">{user.name?.[0]?.toUpperCase() || "U"}</div>
              <div className="hidden sm:block">{user.name}</div>
            </div>
          ) : (
            <button onClick={onOpenLogin} className="px-3 py-2 rounded bg-blue-600 text-white text-sm">Войти</button>
          )}
        </div>
      </nav>
    </header>
  );
}

function Sidebar({ collapsed, onClose }) {
  return (
    <aside
      className={`fixed left-0 top-0 h-full w-64 transform transition-transform bg-white/80 dark:bg-gray-900/80 border-r dark:border-gray-800 backdrop-blur z-40 ${
        collapsed ? "-translate-x-full" : "translate-x-0"
      }`}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-6">
          <div className="font-bold">Меню</div>
          <button onClick={onClose} aria-label="close" className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            ✕
          </button>
        </div>
        <nav className="flex flex-col gap-2">
          <button className="px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-left w-full bg-transparent">Обзор</button>
          <button className="px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-left w-full bg-transparent">Планы</button>
          <button className="px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-left w-full bg-transparent">Камеры</button>
          <button className="px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-left w-full bg-transparent">Документы</button>
          <button className="px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-left w-full bg-transparent">Настройки</button>
        </nav>
      </div>
    </aside>
  );
}

function VideoPanel({ streamUrl, meta }) {
  return (
    <div className="bg-white/60 dark:bg-gray-800/60 border rounded p-2 flex flex-col gap-2">
      <div className="flex items-center justify-between px-2">
        <div className="font-medium">Онлайн видео</div>
        <div className="text-sm text-gray-500">{meta?.camera}</div>
      </div>
      <div className="h-64 sm:h-80 bg-black/5 dark:bg-white/5 rounded overflow-hidden">
        {/* Используем HTML5 video как макет; в реальном кейсе можно интегрировать HLS.js или WebRTC */}
        {streamUrl ? (
          <video controls autoPlay muted playsInline className="w-full h-full object-cover">
            <source src={streamUrl} type="video/mp4" />
            Ваш браузер не поддерживает видео.
          </video>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sm text-gray-500">Загрузка видео...</div>
        )}
      </div>
    </div>
  );
}

function ProgressChart({ data }) {
  return (
    <div className="bg-white/60 dark:bg-gray-800/60 border rounded p-4">
      <div className="font-medium mb-2">График выполнения</div>
      <div style={{ width: "100%", height: 240 }}>
        {data ? (
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(value) => `${value}%`} />
              <Line type="monotone" dataKey="complete" stroke="#8884d8" strokeWidth={3} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="w-full h-full flex items-center justify-center">Загрузка...</div>
        )}
      </div>
    </div>
  );
}

function CostsPanel({ costs }) {
  if (!costs) {
    return (
      <div className="bg-white/60 dark:bg-gray-800/60 border rounded p-4">
        <div>Загрузка затрат...</div>
      </div>
    );
  }

  const diff = costs.totalPlanned - costs.totalActual;

  return (
    <div className="bg-white/60 dark:bg-gray-800/60 border rounded p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="font-medium">Текущие затраты</div>
        <div className="text-sm text-gray-500">Обновлено: {new Date().toLocaleDateString()}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="p-3 rounded bg-white/30 dark:bg-gray-900/30">
          <div className="text-sm text-gray-500">План</div>
          <div className="text-xl font-semibold">{fmtCurrency(costs.totalPlanned)}</div>
        </div>
        <div className="p-3 rounded bg-white/30 dark:bg-gray-900/30">
          <div className="text-sm text-gray-500">Факт</div>
          <div className="text-xl font-semibold">{fmtCurrency(costs.totalActual)}</div>
        </div>
      </div>

      <div>
        <div className="text-sm text-gray-500">Отклонение</div>
        <div className={`text-lg font-semibold ${diff >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtCurrency(diff)}</div>
      </div>

      <div>
        <div className="text-sm text-gray-500 mb-2">Детализация</div>
        <ul className="flex flex-col gap-2">
          {costs.breakdown.map((b) => (
            <li key={b.name} className="flex justify-between text-sm">
              <span>{b.name}</span>
              <span className="font-medium">{fmtCurrency(b.amount)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function LoginModal({ onClose, onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await mockLogin({ username, password });
    setLoading(false);
    if (res.ok) {
      onLogin(res.user, res.token);
      onClose();
    } else {
      setError(res.error || "Ошибка входа");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded shadow-lg w-full max-w-md p-6">
        <h3 className="text-lg font-semibold mb-4">Вход в СтройАгент</h3>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="px-3 py-2 rounded border dark:border-gray-700 bg-transparent"
            placeholder="Имя пользователя"
            required
          />
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            className="px-3 py-2 rounded border dark:border-gray-700 bg-transparent"
            placeholder="Пароль"
            required
          />
          {error && <div className="text-red-500 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded">Отмена</button>
            <button disabled={loading} className="px-4 py-2 rounded bg-blue-600 text-white">{loading ? "Вход..." : "Войти"}</button>
          </div>
        </form>
        <div className="text-xs text-gray-500 mt-3">Подсказка для макета: admin / password</div>
      </div>
    </div>
  );
}

// ----------------- Главный компонент страницы -----------------
export default function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [video, setVideo] = useState({ url: null, meta: null });
  const [progressData, setProgressData] = useState(null);
  const [costs, setCosts] = useState(null);

  const [loginOpen, setLoginOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Загрузка данных с mock API
    let mounted = true;
    (async () => {
      const v = await getVideoStreamUrl();
      const p = await fetchProgressData();
      const c = await fetchCosts();
      if (!mounted) return;
      setVideo(v);
      setProgressData(p);
      setCosts(c);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  function handleLogin(userData, token) {
    // В реальном приложении: хранить токен в cookie/secure storage и передавать в заголовках
    setUser({ name: userData.name, token });
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <TopBar onToggleSidebar={() => setSidebarCollapsed((s) => !s)} user={user} onOpenLogin={() => setLoginOpen(true)} />

      <Sidebar collapsed={sidebarCollapsed} onClose={() => setSidebarCollapsed(true)} />

      <main className="pt-4 px-4 sm:px-6 lg:px-8 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 flex flex-col gap-4">
            <VideoPanel streamUrl={video.url} meta={video.meta} />

            {/* <ProgressChart data={progressData} /> */}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white/60 dark:bg-gray-800/60 border rounded p-4">{/* Дополнительный блок: быстрые задачи */}
                <div className="font-medium mb-2">Текущие задачи</div>
                <ul className="text-sm space-y-2">
                  <li>Монтаж каркаса — <span className="text-gray-500">в процессе</span></li>
                  <li>Доставка бетона — <span className="text-gray-500">завершено</span></li>
                  <li>Прокладка коммуникаций — <span className="text-gray-500">ожидает</span></li>
                </ul>
              </div>

              <div className="bg-white/60 dark:bg-gray-800/60 border rounded p-4">{/* Лог активности */}
                <div className="font-medium mb-2">Журнал активности</div>
                <ul className="text-sm space-y-2 text-gray-600 dark:text-gray-300">
                  <li>10:20 — Проверка креплений, инженер Петров</li>
                  <li>09:45 — Выгрузка материалов — 20 т., подрядчик ООО "Бригадир"</li>
                  <li>08:05 — Начало смены</li>
                </ul>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1 flex flex-col gap-4">
            <CostsPanel costs={costs} />

            <div className="bg-white/60 dark:bg-gray-800/60 border rounded p-4">
              <div className="font-medium mb-2">Короткие ссылки</div>
              <div className="flex flex-col gap-2 text-sm">
                <button className="text-left bg-transparent"><span className="hover:underline">Добавить фото/видео</span></button>
                <button className="text-left bg-transparent"><span className="hover:underline">Создать задачу</span></button>
                <button className="text-left bg-transparent"><span className="hover:underline">Экспорт отчёта</span></button>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} onLogin={handleLogin} />}
    </div>
  );
}
